import {
  Box,
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { ChipCustom, GvCustomMenu } from "../../../styles/styledComponents";
import {
  EditPencilIcon,
  InfoIcon,
  SocStatusCustomIcon,
} from "../../../components/Icons";
import {
  GeneralCareItem,
  IPetItemResponse,
} from "../../../services/Standard-of-care/standard-of-care.interface";
import {
  PetCareCardContainer,
  PetCareDetailsCardContainer,
  PetListCard,
} from "./CarePortal.style";
import React, { useEffect, useState } from "react";
import { colorTheme, getSOCStatusDataMap } from "../../../utils/SOCUtils";

import { AvatarCustom } from "../../../components";
import { CustomToolTip } from "../EditHospitalContainer/EditHospitalContainer.style";
import { LIST_GENDERS, MODALS } from "../../../config/params";
import { MIXPANEL_EVENTS } from "../../../config/mixPanelEvents";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { PAGES_PATH } from "../../../navigation/params";
// eslint-disable-next-line no-unused-vars
import SocAddIcon from "../../../components/Icons/SocAddIcon";
import { formatDateFromUnixTimestamp } from "../../../utils/unix-timestamp";
import { isWellnessMember } from "../../../utils/wellness";
import { useNavigate } from "react-router-dom";
import { usePetStore } from "../../../hooks/redux-hooks/pet-store/pet-store.service";
import { userActivityLogger } from "../../../utils/mixpanel";
// import ViewPetProfile from "../../../components/Icons/ViewPetProfile";
import ClosePetIcon from "../../../components/Icons/ClosePetIcon";
import { SparkleIcon } from "../../../components/Icons";
import { useModal } from "../../../context/ModalContext";
import { useRetirePetStore } from "../../../hooks/redux-hooks/pet-retire-store/pet-retire-store";
// eslint-disable-next-line no-unused-vars
import SocOverdueIcon from "../../../components/Icons/SocOverdueIcon";
import { useOverdueInvoiceStore } from "../../../hooks/redux-hooks/overdue-invoice-store/overdue-invoice.store";
import { useFetchWellnessPlanByPetId } from "../../../services/wellness-plan/wellness-plan.service";

type IPetCardProps = {
  petData: IPetItemResponse;
  petList: IPetItemResponse[];
  handlePetChange: (petId: string) => void;
  careData: GeneralCareItem;
};

const PetProfileCard = (props: IPetCardProps) => {
  const { petData, petList, handlePetChange } = props;
  const navigate = useNavigate();
  const { petList: petStoreList } = usePetStore();
  const { openModal, setRetirePetId } = useModal();
  const { getWellnessPlanByPetId, data: wellnessPlanData, loading: wellnessPlanLoading } =
    useFetchWellnessPlanByPetId();
  const { setPetName, setPetId, setRetireReason, setReasonDescription } =
    useRetirePetStore();
  const {
    pendingPaymentPetList,
    cancelledPetListWithoutOverdue,
    scratchpayOverdueNoticePetIds,
  } = useOverdueInvoiceStore();
  // eslint-disable-next-line no-unused-vars
  const [isPetPaymentOverdue, setIsPetPaymentOverdue] =
    useState<boolean>(false);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handlePetMenu = (event: any) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOpenPetEditProfile = () => {
    userActivityLogger(MIXPANEL_EVENTS.USER_CLICK_PET_CARD, {
      pet_name: petData.name,
      referred_from: "Dashboard",
    });
    navigate(`${PAGES_PATH.PET_PROFILE}?id=${petData?.id}`);
  };

  const handleOpenManagePlan = () => {
    handleClose();
    navigate(`${PAGES_PATH.MANAGE_PLAN}?id=${petData.id}`);
  };

  const handleOpenRetirePetProfile = () => {
    if (!isPetPaymentOverdue) {
      setPetName(petData.name);
      setPetId(petData.id);
      setRetireReason("");
      setReasonDescription("");
      userActivityLogger(MIXPANEL_EVENTS.RETIRE_PET_PROFILE, {
        pet_name: petData.name,
      });
      openModal(MODALS.CLOSE_PET_MODAL);
    } else {
      setRetirePetId(petData.id);
      openModal(MODALS.PENDING_INVOICE_ALERT_MODAL);
    }
  };

  // Legacy: controls whether "Close Pet Profile" should be blocked.
  // Keep tied to legacy delinquency store only (not ScratchPay overdue notice).
  useEffect(() => {
    if (pendingPaymentPetList) {
      const pendingInvoiceData = pendingPaymentPetList.filter(
        (petDetails: any) => petData.id === petDetails.petId,
      );

      setIsPetPaymentOverdue(pendingInvoiceData.length !== 0);
    }
  }, [petData, pendingPaymentPetList]);

  useEffect(() => {
    getWellnessPlanByPetId(petData.id);
  }, [petData.id]);

  const isWellnessPlanActive =
    Boolean(wellnessPlanData?.isSubscriptionActive) ||
    Boolean(petStoreList && isWellnessMember(petStoreList, petData.id));

  const isManagePlanReady =
    !wellnessPlanLoading &&
    Boolean(wellnessPlanData?.isSubscriptionActive) &&
    (wellnessPlanData?.wellnessplanBenefitUsage?.length ?? 0) > 0;

  const scratchpayOverdueNoticePetIdSet = new Set(
    (scratchpayOverdueNoticePetIds ?? []).map((id) => String(id)),
  );

  const getGender = (petGender: string | undefined) => {
    const gender = LIST_GENDERS.find((gender) => gender.value === petGender);

    return gender ?
      gender.label :
      petGender === "2" ?
        "Male" :
        petGender === "4" ?
          "Female" :
          undefined;
  };

  const petDetailMapData = [
    {
      key: "Birthday",
      value:
        petData?.age === null ?
          "N/A" :
          formatDateFromUnixTimestamp(Number(petData?.birthdate)),
    },
    { key: "Breed", value: petData?.breedName ? petData?.breedName : "Other" },
    { key: "Sex", value: getGender(petData?.gender) },
    { key: "Color", value: petData?.colorName ? petData?.colorName : "N/A" },
    { key: "Neutered", value: petData?.isDesexed ? "Yes" : "No" },
  ];

  const isMediumScreen = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md"),
  );

  const isPetPlanCancelled = (petId: string)=> cancelledPetListWithoutOverdue.filter((petDetails: any) =>
    petId === petDetails.petId).length !== 0;

  const isScratchpayOverdueNotice = (petId: string) =>
    scratchpayOverdueNoticePetIdSet.has(String(petId));

  return (
    <PetCareCardContainer>
      <PetCareDetailsCardContainer>
        <IconButton
          className="!absolute !top-3.5 !right-4 !cursor-pointer !text-black"
          id="petEdit-button"
          aria-haspopup="true"
          aria-controls={open ? "petEdit-menu" : undefined}
          aria-expanded={open ? "true" : undefined}
          onClick={handlePetMenu}
        >
          <MoreHorizIcon />
        </IconButton>

        <GvCustomMenu
          autoFocus={false}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          id="petEdit-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            "aria-labelledby": "petEdit-button",
          }}
        >
          <MenuItem onClick={handleOpenPetEditProfile}>
            <ListItemIcon>
              <EditPencilIcon />
            </ListItemIcon>
            <ListItemText>Edit Pet Profile</ListItemText>
          </MenuItem>

          {isWellnessPlanActive && (
            <MenuItem onClick={handleOpenManagePlan} disabled={!isManagePlanReady}>
              <ListItemIcon>
                {wellnessPlanLoading ?
                  <CircularProgress size={18} thickness={4} sx={{ color: "black.main" }} /> :
                  <SparkleIcon width={24} height={24} />
                }
              </ListItemIcon>
              <ListItemText>Manage Plan</ListItemText>
            </MenuItem>
          )}

          <MenuItem
            onClick={handleOpenRetirePetProfile}
          >
            <ListItemIcon>
              <ClosePetIcon />
            </ListItemIcon>
            <ListItemText>Close Pet Profile</ListItemText>
          </MenuItem>
        </GvCustomMenu>

        <Box className="pet_avatar_soc">
          <div className="relative">
            <AvatarCustom
              width={isMediumScreen ? 56 : 108}
              height={isMediumScreen ? 56 : 108}
              image={petData?.image}
              name={petData?.name}
              isBorder={
                colorTheme[petData?.socStatus as keyof typeof colorTheme]
                  ?.color || colorTheme["default"]
                  ?.color
              }
            />
            {!petData?.image && (
              <div className="absolute top-0 right-1">
                <CustomToolTip
                  title={
                    <Typography color="inherit">
                      Complete{` ${petData?.name}'s`} profile.
                    </Typography>
                  }
                  placement="top"
                  sx={{ marginLeft: "1rem !important" }}
                >
                  <Box className="cursor-pointer">
                    <img
                      src="/assets/icons/Notification bubble.svg"
                      className="!w-4 !h-4 mt-2"
                      alt={"info"}
                    />
                  </Box>
                </CustomToolTip>
              </div>
            )}
          </div>
          <div className="flex flex-col medium:flex-row items-center gap-1">
            <Typography
              variant="body1"
              className="flex"
              fontWeight={600}
            >
              {petData?.name}
              {petStoreList && isWellnessMember(petStoreList, petData.id) && (
                <span className="rounded-full mx-1">
                  {cancelledPetListWithoutOverdue.filter(
                    (petDetails: any) => petData.id === petDetails.petId,
                  ).length !== 0 ? (
                      <SocOverdueIcon transformX="0" transformY="0" />
                    ) : (
                      <SocAddIcon/>
                    )}
                </span>
              )}
            </Typography>
          </div>
          <ChipCustom
            icon={<SocStatusCustomIcon type={petData?.socStatus as any} />}
            label={getSOCStatusDataMap(petData?.socStatus).label}
            className="!font-semibold !text-xs !py-[0.375rem]
             !px-2"
            color={getSOCStatusDataMap(petData?.socStatus).color}
            size="small"
          />
        </Box>

        <Box className="pet_details gap-2">
          {petDetailMapData.map((item) => (
            <Box className="info_section" key={item.key}>
              <Typography variant="h4">{item.key}:</Typography>
              <Typography variant="body1" fontWeight={600}>
                {item.value}
              </Typography>
            </Box>
          ))}
        </Box>
      </PetCareDetailsCardContainer>

      <div
        id="pet_list_card"
        className="h-min max-h-[30rem] md:mb-8 w-full overflow-y-auto pr-2 scroll-hide
      md:overflow-y-auto"
      >
        {petList.length &&
          petList
            .filter((item) => item.id !== petData.id)
            .map((pet: any) => (
              <PetListCard
                key={pet.id}
                onClick={() => handlePetChange && handlePetChange(pet.id)}
              >
                <div className="w-full flex flex-col gap-3">
                  <div className="flex gap-3 items-center">
                    <AvatarCustom
                      width={isMediumScreen ? 40 : 56}
                      height={isMediumScreen ? 40 : 56}
                      image={pet?.image}
                      name={pet?.name}
                      isWellnessMember={pet?.items?.wellnessPlanStatus && !isPetPlanCancelled(pet.id)}
                      isPlanOverdue={pet?.items?.wellnessPlanStatus && isPetPlanCancelled(pet.id)}
                      wellnessIconHeight={22}
                      wellnessIconWidth={22}
                      isBorder={
                        colorTheme[pet?.socStatus as keyof typeof colorTheme]
                          ?.color || "default"
                      }
                    />
                    <Box className="info">
                      <div className="flex gap-1">
                        <Typography variant={isMediumScreen ? "subtitle1" : "h4"}>
                          {pet?.name}
                        </Typography>
                        {/* Legacy Square delinquency tooltip */}
                        {pet?.isPlanOverdue && !isScratchpayOverdueNotice(pet.id) && (
                          <CustomToolTip
                            title={
                              <Typography color="inherit">
                                Pay Outstanding Payment
                              </Typography>
                            }
                            placement="top"
                            sx={{ marginLeft: "2rem !important" }}
                          >
                            <Box className="text-yellow-500 text-lg font-normal leading-6 cursor-pointer">
                              <InfoIcon
                                width={20}
                                height={20}
                                svgStyle={{ marginTop: "0.1rem" }}
                                color="#D35633"
                              />
                            </Box>
                          </CustomToolTip>
                        )}
                      </div>
                      <ChipCustom
                        icon={<SocStatusCustomIcon type={pet.socStatus} />}
                        label={getSOCStatusDataMap(pet.socStatus).label}
                        className="!font-semibold !text-xs !py-[0.375rem] !px-2"
                        color={getSOCStatusDataMap(pet.socStatus).color}
                        size="small"
                      />
                    </Box>
                  </div>
                  {/* ScratchPay overdue notice highlight */}
                  {isScratchpayOverdueNotice(pet.id) && (
                    <div className="w-full bg-red-3 text-red-4 rounded py-1 text-center text-sm font-medium">
                      Wellness Plan Payment Overdue
                    </div>
                  )}
                </div>
              </PetListCard>
            ))}
      </div>
    </PetCareCardContainer>
  );
};

export default PetProfileCard;
